
import React, { useState } from 'react';
import { CloseIcon } from './Icons';
import { apiService } from '../../services/apiService';
import { friendlyAuthError } from '../../utils/authErrors';
import { InlineAlert } from '../../components/InlineAlert';

interface SupportModalProps {
    onClose: () => void;
}

const SupportModal: React.FC<SupportModalProps> = ({ onClose }) => {
    const assistanceAreas = [
        "Open Innovation Challenges",
        "Assistance with University Connect",
        "IP Financing",
        "IP Valuation",
        "IP Strategy & Evaluation",
        "Legal, Contracts, Due Diligence",
        "Market Readiness Assessment",
        "Technology Competitive Landscape",
        "Others"
    ];

    const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const toggleArea = (area: string) => {
        setSelectedAreas(prev => 
            prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]
        );
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.name.trim() || !formData.email.trim() || selectedAreas.length === 0) {
            setSubmitStatus('error');
            setErrorMessage('Please fill in all required fields and select at least one assistance area.');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('idle');
        setErrorMessage('');

        try {
            const response = await apiService.createConciergeCall({
                name: formData.name,
                email: formData.email,
                areas: selectedAreas,
                message: formData.message || `I need assistance with: ${selectedAreas.join(', ')}`,
                form_source: 'support_modal',
            });

            if (response.success) {
                setSubmitStatus('success');
                // Close modal after showing success for a moment
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                setSubmitStatus('error');
                setErrorMessage(friendlyAuthError(response.error, 'Failed to submit request. Please try again.'));
            }
        } catch (error) {
            setSubmitStatus('error');
            setErrorMessage('Network error. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid = formData.name.trim() && formData.email.trim() && selectedAreas.length > 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 font-space-grotesk p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl p-6 relative" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-grayscale-1200 text-white hover:bg-grayscale-1100 transition-colors">
                    <CloseIcon />
                </button>
                <h3 className="text-base font-medium text-grayscale-1300 mb-4">
                    Need Further Assistance? Book a free call with our concierge
                </h3>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="name" className="block text-xs text-grayscale-1200 mb-1">Name:</label>
                            <input 
                                id="name" 
                                name="name"
                                type="text" 
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="John Doe" 
                                className="w-full p-3 text-sm bg-[#F5F5F5] rounded-md placeholder-grayscale-600 focus:outline-none focus:ring-1 focus:ring-primary-600" 
                            />
                        </div>
                        <div>
                            <label htmlFor="support-email" className="block text-xs text-grayscale-1200 mb-1">Email:</label>
                            <input 
                                id="support-email" 
                                name="email"
                                type="email" 
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="johndoe@abccompany.com" 
                                className="w-full p-3 text-sm bg-[#F5F5F5] rounded-md placeholder-grayscale-600 focus:outline-none focus:ring-1 focus:ring-primary-600" 
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs text-grayscale-1200 mb-2">Please select which area(s) you are looking for information and assistance in:</label>
                        <div className="flex flex-wrap gap-2">
                            {assistanceAreas.map(area => (
                                <button
                                    key={area}
                                    onClick={() => toggleArea(area)}
                                    className={`px-4 py-1 text-xs rounded border transition-colors ${
                                        selectedAreas.includes(area)
                                        ? 'bg-[#EAF1FB] border-[#3568B3] text-[#2C5695] font-medium'
                                        : 'bg-white border-grayscale-300 text-grayscale-900'
                                    }`}
                                >
                                    {area}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder="Write a custom message"
                            className="w-full h-24 p-3 text-sm bg-[#F5F5F5] rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-600 resize-none"
                            maxLength={600}
                        />
                        <p className="text-right text-xs text-grayscale-800 italic mt-1">
                            {formData.message.length}/600 chars
                        </p>
                    </div>

                    {/* Status Messages */}
                    {submitStatus === 'success' && (
                        <InlineAlert variant="success" className="mt-4">
                            Request submitted successfully! Our concierge team will contact you soon.
                        </InlineAlert>
                    )}
                    {submitStatus === 'error' && errorMessage && (
                        <InlineAlert variant="error" className="mt-4">{errorMessage}</InlineAlert>
                    )}

                    <div className="text-center">
                        <button 
                            onClick={handleSubmit}
                            disabled={!isFormValid || isSubmitting || submitStatus === 'success'}
                            className="w-full px-4 py-2.5 text-sm font-medium text-button-text bg-[#AF8E5E] rounded-full hover:bg-[#9A7A4C] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? 'Submitting...' : submitStatus === 'success' ? 'Submitted!' : 'Submit Request'}
                        </button>
                        <p className="text-xs text-grayscale-1100 italic mt-2">
                            {submitStatus === 'success' ? 'Our team will reach out to schedule a call' : 'Our concierge team will contact you within 7 business days'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupportModal;